import React, { useState } from "react";
import { useFormik } from "formik";
import * as Yup from "yup";
import Swal from "sweetalert2";
import {
  AddLtiIntegration,
  EditLtiIntegration,
  TestLtiIntegration,
} from "../../../services/LtiIntegrationService";
import { API_BASE } from "../../../utils/Constants";
import { useHistory, useParams } from "react-router-dom";

/**
 * Builds default redirect URL from the current API base.
 */
function getDefaultRedirectUrl() {
  const baseUrl = String(API_BASE || "").trim().replace(/\/$/, "");
  return baseUrl ? `${baseUrl}/lti/oidc/redirect` : "";
}

/**
 * Extracts a readable API error message from axios responses.
 */
function getApiErrorMessage(error, fallbackMessage) {
  const payload = error?.response?.data;

  if (Array.isArray(payload) && payload.length) {
    return payload.map((item) => item?.message).filter(Boolean).join("\n");
  }

  if (payload?.errors?.length) {
    return payload.errors.map((item) => item?.message).filter(Boolean).join("\n");
  }

  return payload?.message || fallbackMessage;
}

/**
 * Add/edit form for one LTI integration.
 */
const IntegrationForm = ({ mode, integrationId, initialData, onSaved }) => {
  const [testing, setTesting] = useState(false);
  const { tab } = useParams();
  const history = useHistory();

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      integration_name: initialData?.integration_name || "",
      client_id: initialData?.client_id || "",
      service_endpoint: initialData?.service_endpoint || "",
      public_keyset: initialData?.public_keyset || "",
      initiate_login_url: initialData?.initiate_login_url || "",
      redirect_url: initialData?.redirect_url || getDefaultRedirectUrl(),
    },
    validationSchema: Yup.object({
      integration_name: Yup.string().required("Integration Name is required"),
      service_endpoint: Yup.string()
        .url("Must be a valid URL")
        .required("Service Endpoint is required"),
      public_keyset: Yup.string().required("Public Keyset is required"),
      initiate_login_url: Yup.string()
        .url("Must be a valid URL")
        .required("Initiate Login URL is required"),
      redirect_url: Yup.string()
        .url("Must be a valid URL")
        .required("Redirect URL is required"),
    }),
    onSubmit: async (values, { setSubmitting }) => {
      try {
        setSubmitting(true);
        let response;

        const payload = {
          integration_name: values.integration_name,
          service_endpoint: values.service_endpoint,
          public_keyset: values.public_keyset,
          initiate_login_url: values.initiate_login_url,
          redirect_url: values.redirect_url,
        };

        if (mode === "open") {
          response = await EditLtiIntegration({
            id: integrationId,
            ...payload,
          });
        } else {
          response = await AddLtiIntegration(payload);
        }

        Swal.fire({
          icon: "success",
          title: "Success",
          text: "Integration saved successfully.",
        });
        onSaved(response?.data?.integration || null);
      } catch (error) {
        Swal.fire({
          icon: "error",
          title: "Error",
          text: getApiErrorMessage(error, "Something Went Wrong!."),
        });
      } finally {
        setSubmitting(false);
      }
    },
  });

  /**
   * Calls backend test endpoint and shows result.
   */
  const handleTest = async () => {
    if (!integrationId) {
      Swal.fire({
        icon: "info",
        title: "Save first",
        text: "Please save integration before testing.",
      });
      return;
    }

    try {
      setTesting(true);
      const response = await TestLtiIntegration(integrationId);
      const data = response?.data || {};
      const warnings = data.warnings || [];
      const isSuccess = String(data.message || "")
        .toLowerCase()
        .includes("successful");

      let text = data.message || "Integration test successful.";
      if (typeof data.login_initiated === "boolean") {
        text += `\n\nLTI login initiation: ${data.login_initiated ? "OK" : "Failed"}`;
      }
      if (typeof data.resources_count === "number") {
        text += `\nResources: ${data.resources_count}`;
      }
      if (warnings.length) {
        text += `\n\n${warnings.join("\n")}`;
      }

      Swal.fire({
        icon: warnings.length && !isSuccess ? "warning" : "success",
        title: warnings.length && !isSuccess ? "Partial Success" : "Success",
        text,
      });
    } catch (error) {
      Swal.fire({
        icon: "error",
        title: "Error",
        text: getApiErrorMessage(error, "Integration test failed."),
      });
    } finally {
      setTesting(false);
    }
  };

  return (
    <div className="card ">
      <div className="card-body">
        <div className="card-body-inr card-body-info">
          <form onSubmit={formik.handleSubmit}>
            <div className="row">
              <div className="col-md-6 col-lg-4" title="Integration Name">
                <div className="form-group">
                  <label htmlFor="integration_name">Integration Name *</label>
                  <div className="form-icon-group mb-4">
                    <input
                      type="text"
                      className={"form-control" + (formik.errors.integration_name && formik.touched.integration_name ? " is-invalid" : "")}
                      id="integration_name"
                      name="integration_name"
                      placeholder="Integration Name"
                      value={formik.values.integration_name}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                    />
                  </div>
                  {formik.errors.integration_name && formik.touched.integration_name ? (
                    <span className="exclamation">
                      <i className="fal fa-exclamation-circle"></i>
                    </span>
                  ) : null}
                </div>
              </div>

              <div className="col-md-6 col-lg-4" title="Client ID">
                <div className="form-group">
                  <label htmlFor="client_id">Client (client_id)</label>
                  <div className="form-icon-group mb-4">
                    <input
                      type="text"
                      className="form-control block-action"
                      id="client_id"
                      name="client_id"
                      value={formik.values.client_id}
                      disabled
                      placeholder="Auto-generated Client ID"
                    />
                  </div>
                </div>
              </div>

              <div className="col-md-6 col-lg-4" title="Service Endpoint">
                <div className="form-group">
                  <label htmlFor="service_endpoint">Service Endpoint *</label>
                  <div className="form-icon-group mb-4">
                    <input
                      type="text"
                      className={"form-control" + (formik.errors.service_endpoint && formik.touched.service_endpoint ? " is-invalid" : "")}
                      id="service_endpoint"
                      name="service_endpoint"
                      placeholder="Service Endpoint"
                      value={formik.values.service_endpoint}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                    />
                  </div>
                  {formik.errors.service_endpoint && formik.touched.service_endpoint ? (
                    <span className="exclamation">
                      <i className="fal fa-exclamation-circle"></i>
                    </span>
                  ) : null}
                </div>
              </div>

                            <div className="col-md-6 col-lg-4" title="Public Keyset">
                <div className="form-group">
                  <label htmlFor="public_keyset">Public Keyset *</label>
                  <div className="form-icon-group mb-4">
                    <textarea
                      className={"form-control" + (formik.errors.public_keyset && formik.touched.public_keyset ? " is-invalid" : "")}
                      rows="4"
                      id="public_keyset"
                      name="public_keyset"
                      placeholder="Public Keyset"
                      value={formik.values.public_keyset}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                    />
                  </div>
                  {formik.errors.public_keyset && formik.touched.public_keyset ? (
                    <span className="exclamation">
                      <i className="fal fa-exclamation-circle"></i>
                    </span>
                  ) : null}
                </div>
              </div>

              <div className="col-md-6 col-lg-4" title="Initiate Login URL">
                <div className="form-group">
                  <label htmlFor="initiate_login_url">Initiate Login URL *</label>
                  <div className="form-icon-group mb-4">
                    <input
                      type="text"
                      className={"form-control" + (formik.errors.initiate_login_url && formik.touched.initiate_login_url ? " is-invalid" : "")}
                      id="initiate_login_url"
                      name="initiate_login_url"
                      placeholder="Initiate Login URL"
                      value={formik.values.initiate_login_url}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                    />
                  </div>
                  {formik.errors.initiate_login_url && formik.touched.initiate_login_url ? (
                    <span className="exclamation">
                      <i className="fal fa-exclamation-circle"></i>
                    </span>
                  ) : null}
                </div>
              </div>

              <div className="col-md-6 col-lg-4" title="Redirect URL">
                <div className="form-group">
                  <label htmlFor="redirect_url">Redirect URL *</label>
                  <div className="form-icon-group mb-4">
                    <input
                      type="text"
                      className={"form-control" + (formik.errors.redirect_url && formik.touched.redirect_url ? " is-invalid" : "")}
                      id="redirect_url"
                      name="redirect_url"
                      placeholder="Redirect URL"
                      value={formik.values.redirect_url}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                    />
                  </div>
                  {formik.errors.redirect_url && formik.touched.redirect_url ? (
                    <span className="exclamation">
                      <i className="fal fa-exclamation-circle"></i>
                    </span>
                  ) : null}
                </div>
              </div>
            </div>

            <div className="form-group form-group-save-cancel">
              {tab !== "create" && <button
                className="btn btn-white-bordered"
                type="button"
                title="Test Integration"
                onClick={handleTest}
                disabled={testing || formik.isSubmitting}
              >
                {testing ? <i className="fas fa-cog fa-spin"></i> : <i className="fal fa-vial"></i>}
                Test Integration
              </button>}

              <button
                className="btn btn-save btn-success"
                type="submit"
                title="Save"
                disabled={formik.isSubmitting || testing}
              >
                {formik.isSubmitting ? <i className="fas fa-cog fa-spin"></i> : <i className="fal fa-save"></i>}
                Save
              </button>

              <button
                className="btn btn-close btn-danger"
                type="button"
                title="Cancel"
                onClick={() => tab == "create" ? history.goBack() : formik.resetForm()}
              >
                <i className="fal fa-times"></i>
                Cancel
              </button>
            </div>

            {/* Automatic Validation Messages */}
            {Object.keys(formik.initialValues).map((key) =>
              formik.touched[key] && formik.errors[key] ? (
                <div key={key} className="invalid-feedback d-block">
                  {formik.errors[key]}
                </div>
              ) : null
            )}
          </form>
        </div>
      </div>
    </div>
  );
};

export default IntegrationForm;